'use client';

/**
 * ProjectHeader
 *
 * Header component for project detail page showing:
 * - Back link to projects list
 * - Project name and slug
 * - Organization link
 */

import { Button, Heading, Inline, Stack, Text } from '@cortex/ui';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  slug: string;
  repo_url: string | null;
  organizations?: {
    id: string;
    name: string;
    slug: string;
  };
}

interface ProjectHeaderProps {
  project: Project;
}

export function ProjectHeader({ project }: ProjectHeaderProps) {
  const navigate = useNavigate();

  return (
    <Stack gap="md">
      {/* Back link */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate({ to: '/admin/projects' })}
        className="-ml-2 gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Projects
      </Button>

      {/* Project info */}
      <Inline align="start" justify="between">
        <Stack gap="none">
          <Heading level={1} className="text-2xl font-bold">
            {project.name}
          </Heading>
          <Inline gap="md" className="mt-1">
            <Text color="muted" size="sm">
              {project.slug}
            </Text>
            {project.organizations && (
              <Text color="muted" size="sm">
                &bull;{' '}
                <Button
                  variant="link"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: '/admin/organizations/$slug',
                      params: { slug: project.organizations!.slug },
                    })
                  }
                  className="h-auto p-0"
                >
                  {project.organizations.name}
                </Button>
              </Text>
            )}
          </Inline>
        </Stack>

        {/* Repository link */}
        {project.repo_url && (
          <Button
            variant="link"
            asChild
            className="text-primary flex items-center gap-1 text-base hover:underline"
          >
            <a
              href={project.repo_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Repository
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        )}
      </Inline>
    </Stack>
  );
}
